import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { TradingEntityDetail } from 'api'
import { Locale, Translation } from 'locale'
import {
  createGroupedActionRenderer,
  createActionsRenderer,
  gridOptionsBase,
  groupingRowColDef,
  StatusCellRenderer,
  TypedValueGetterParams,
  withAllContext
} from 'view/components/datagrid'
import { TradingEntityActionsButton } from './TradingEntityActionsButton'
import {
  booleanFormatter,
  getEntityStatusActiveSuspendedText,
  getEntityStatusColor,
  tradingEntityTypesFormatter
} from 'view/shared/datagrid'

enum TradingEntityGridColumnId {
  TRADING_ENTITY_CHECKBOX = 'TRADING_ENTITY_CHECKBOX',
  TRADING_ENTITY_ACTIONS = 'TRADING_ENTITY_ACTIONS',
  TRADING_ENTITY_STATUS = 'TRADING_ENTITY_STATUS',
  TRADING_ENTITY_NAME = 'TRADING_ENTITY_NAME',
  TRADING_ENTITY_SHORT_NAME = 'TRADING_ENTITY_SHORT_NAME',
  TRADING_ENTITY_FICC_SHORT_NAME = 'TRADING_ENTITY_FICC_SHORT_NAME',
  TRADING_ENTITY_LEI = 'TRADING_ENTITY_LEI',
  TRADING_ENTITY_BIC = 'TRADING_ENTITY_BIC',
  TRADING_ENTITY_ORGANISATION = 'TRADING_ENTITY_ORGANISATION',
  TRADING_ENTITY_TYPE = 'TRADING_ENTITY_TYPE',
  TRADING_ENTITY_15A6_ELIGIBLE = 'TRADING_ENTITY_15A6_ELIGIBLE',
  TRADING_ENTITY_COMBINED_EU_US_EOD_FILE = 'TRADING_ENTITY_COMBINED_EU_US_EOD_FILE',
  TRADING_ENTITY_CENTRAL_BANK = 'TRADING_ENTITY_CENTRAL_BANK',
  TRADING_ENTITY_NEGOTIATION_HISTORY_FILE = 'TRADING_ENTITY_NEGOTIATION_HISTORY_FILE',
  TRADING_ENTITY_EU_GFID = 'TRADING_ENTITY_EU_GFID',
  TRADING_ENTITY_US_GFID = 'TRADING_ENTITY_US_GFID',
  TRADING_ENTITY_OPERATING_COUNTRY = 'TRADING_ENTITY_OPERATING_COUNTRY'
}

export const TradingEntityColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    resizable: false,
    sortable: false
  },
  groupingRowColDef,
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 130,
    minWidth: 130,
    headerComponent: withAllContext(createGroupedActionRenderer(TradingEntityActionsButton)),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(TradingEntityActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_STATUS,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) =>
      params.data && getEntityStatusActiveSuspendedText(params.data.tradingEntity.status),
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 200,
    filter: 'agSetColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: TradingEntityDetail) => getEntityStatusColor(data.tradingEntity.status)
    },
    equals: () => false,
    lockVisible: true
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_NAME,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.publishedName,
    headerName: Locale.translate(Translation.TC_PUBLISHED_NAME),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_SHORT_NAME,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.shortName,
    headerName: Locale.translate(Translation.TC_SHORT_NAME_DEFAULT),
    headerTooltip: Locale.translate(Translation.SC_SHORT_NAME),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_FICC_SHORT_NAME,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.ficcShortName,
    headerName: Locale.translate(Translation.TC_FICC_NAME_DEFAULT),
    headerTooltip: Locale.translate(Translation.SC_FICC_SHORT_NAME),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_LEI,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.lei,
    headerName: Locale.translate(Translation.TC_LEI),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_BIC,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.bic,
    headerName: Locale.translate(Translation.TC_BIC),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_ORGANISATION,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) =>
      params.data && params.data.organisation && params.data.organisation.legalName,
    headerName: Locale.translate(Translation.TC_ORGANISATION),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_TYPE,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.tradingEntityTypes,
    valueFormatter: tradingEntityTypesFormatter,
    headerName: Locale.translate(Translation.TC_TRADING_ENTITY_TYPE),
    width: 200,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_15A6_ELIGIBLE,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.eligible15a6,
    valueFormatter: booleanFormatter,
    headerName: Locale.translate(Translation.TC_15A6_ELIGIBLE),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_COMBINED_EU_US_EOD_FILE,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.eligibleCombinedRecFile,
    valueFormatter: booleanFormatter,
    headerName: Locale.translate(Translation.TC_COMBINED_EU_US_EOD_FILE),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_CENTRAL_BANK,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.centralBank,
    valueFormatter: booleanFormatter,
    headerName: Locale.translate(Translation.TC_CENTRAL_BANK),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_NEGOTIATION_HISTORY_FILE,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.negotiationHistoryFile,
    valueFormatter: booleanFormatter,
    headerName: Locale.translate(Translation.TC_NEGOTIATION_HISTORY_FILE),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_EU_GFID,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.euGfidCode,
    headerName: Locale.translate(Translation.TC_EU_GFID),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_US_GFID,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.usGfidCode,
    headerName: Locale.translate(Translation.TC_US_GFID),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityGridColumnId.TRADING_ENTITY_OPERATING_COUNTRY,
    valueGetter: (params: TypedValueGetterParams<TradingEntityDetail>) => params.data && params.data.tradingEntity.operatingCountry,
    headerName: Locale.translate(Translation.TC_OPERATING_COUNTRY),
    width: 180,
    filter: 'agSetColumnFilter'
  }
]

export const tradingEntityGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.tradingEntity.id
}
